import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  Message,
  MessageList,
  MessageListRes,
} from "../../types/MessageListType";
import { SingleMessageRes } from "../../types/SingleMessageType";
import insertDateDividers from "../../utils/insertDateDividers";
import {
  MESSAGE_WINDOW_MAX,
  countRealMessages,
  isHiddenDeletedMessage,
  isRealMessage,
  withoutHiddenDeleted,
} from "../../utils/messageListWindow";
import { isPinnedCatalogAuthoritative } from "../../utils/fetchPinnedMessages";

function pickPinnedCatalog(
  incoming: MessageListRes | undefined,
  previous: MessageList,
): MessageList {
  const pins = incoming?.pinned_messages;
  const authoritative =
    isPinnedCatalogAuthoritative(incoming as any) ||
    isPinnedCatalogAuthoritative({ pinned_messages: pins } as any);
  if (!authoritative) return previous;
  return pins || previous;
}

type MessageListState = MessageListRes & { snappingIds: number[] };

// Define the initial state with an empty MessageList
const initialState: MessageListState = {
  snappingIds: [],
  message_list: {
    Records: [],
    Pagination: {
      total_pages: 0,
      total_records: 0,
      current_page: 0,
      records_per_page: 0,
    },
  },
  pinned_messages: {
    Records: [],
    Pagination: {
      total_pages: 0,
      total_records: 0,
      current_page: 0,
      records_per_page: 0,
    },
  },
};

function stripDateDividers(records: Message[]) {
  return records.filter((msg) => msg.message_type !== "date");
}

/** Trim from the newest end (keep older). Returns whether trim happened. */
function trimNewest(records: Message[], max = MESSAGE_WINDOW_MAX): Message[] {
  const real = stripDateDividers(records);
  if (real.length <= max) return insertDateDividers(real);
  return insertDateDividers(real.slice(0, max));
}

/** Trim from the oldest end (keep newer). */
function trimOldest(records: Message[], max = MESSAGE_WINDOW_MAX): Message[] {
  const real = stripDateDividers(records);
  if (real.length <= max) return insertDateDividers(real);
  return insertDateDividers(real.slice(real.length - max));
}

// Create a slice of the state
const MessageListSlice = createSlice({
  name: "MessageList",
  initialState,
  reducers: {
    // Reducer to update the MessageList
    updateMessageList(state, action: PayloadAction<MessageListRes>) {
      const incomingRecords = withoutHiddenDeleted(
        action.payload.message_list.Records,
      );
      const pinned_messages = pickPinnedCatalog(
        action.payload,
        state.pinned_messages,
      );

      if (
        state.message_list.Records.length === 0 ||
        incomingRecords.length === 0 ||
        state.message_list.Records[0]?.chat_id !== incomingRecords[0]?.chat_id
      ) {
        const merged = [...incomingRecords].reverse();
        return {
          ...action.payload,
          snappingIds: state.snappingIds,
          pinned_messages,
          message_list: {
            ...action.payload.message_list,
            Records: insertDateDividers(merged),
          },
        };
      }

      const existingMessageIds = new Set(
        state.message_list.Records.map((msg) => msg.message_id),
      );

      const newMessages = incomingRecords.filter(
        (msg) => !existingMessageIds.has(msg.message_id),
      );

      const merged = [...newMessages]
        .reverse()
        .concat(state.message_list.Records);
      const cleaned = stripDateDividers(merged);

      return {
        ...action.payload,
        snappingIds: state.snappingIds,
        pinned_messages,
        message_list: {
          ...action.payload.message_list,
          Records: trimOldest(cleaned),
        },
      };
    },

    /** Replace the visible window (around-jump / return to tip). */
    replaceMessageWindow(state, action: PayloadAction<MessageListRes>) {
      const incomingRecords = withoutHiddenDeleted(
        action.payload.message_list.Records || [],
      );
      const merged = [...incomingRecords].reverse();
      const pinned_messages = pickPinnedCatalog(
        action.payload,
        state.pinned_messages,
      );

      return {
        ...state,
        pinned_messages,
        message_list: {
          ...action.payload.message_list,
          Records: insertDateDividers(merged),
        },
      };
    },

    /** Authoritative pin catalog replace (get_pinned_messages / tip embed). */
    replacePinnedCatalog(
      state,
      action: PayloadAction<{
        chat_id?: number;
        pinned_messages: MessageList;
        complete?: boolean;
      }>,
    ) {
      const authoritative =
        action.payload.complete === true ||
        isPinnedCatalogAuthoritative(action.payload as any);
      if (!authoritative) return;
      state.pinned_messages = action.payload.pinned_messages;
    },

    /** Soft warm from disk — never clears a richer in-memory catalog. */
    warmPinnedCatalog(state, action: PayloadAction<Message[]>) {
      if (!action.payload?.length) return;
      if (state.pinned_messages.Records.length > 0) return;
      state.pinned_messages = {
        ...state.pinned_messages,
        Records: action.payload,
      };
    },

    /** Prepend older page (API newest-first / DESC). May trim newest. */
    prependOlderMessages(state, action: PayloadAction<Message[]>) {
      const existingIds = new Set(
        state.message_list.Records.map((msg) => msg.message_id),
      );
      const incoming = withoutHiddenDeleted(action.payload).filter(
        (msg) => isRealMessage(msg) && !existingIds.has(msg.message_id),
      );
      if (incoming.length === 0) return;

      const merged = [...incoming]
        .reverse()
        .concat(stripDateDividers(state.message_list.Records));
      state.message_list.Records = trimNewest(merged);
      if (countRealMessages(state.message_list.Records) >= MESSAGE_WINDOW_MAX) {
        state.message_list.Pagination = {
          ...state.message_list.Pagination,
          has_more_newer: true,
        };
      }
    },

    /** Append newer page (API newest-first / DESC). May trim oldest. */
    appendNewerMessages(state, action: PayloadAction<Message[]>) {
      const existingIds = new Set(
        state.message_list.Records.map((msg) => msg.message_id),
      );
      const incoming = withoutHiddenDeleted(action.payload).filter(
        (msg) => isRealMessage(msg) && !existingIds.has(msg.message_id),
      );
      if (incoming.length === 0) return;

      const merged = stripDateDividers(state.message_list.Records).concat(
        [...incoming].reverse(),
      );
      state.message_list.Records = trimOldest(merged);
      if (countRealMessages(state.message_list.Records) >= MESSAGE_WINDOW_MAX) {
        state.message_list.Pagination = {
          ...state.message_list.Pagination,
          has_more_older: true,
        };
      }
    },

    removeMessageList() {
      return initialState; //Remove Data from messagelist when user click on ther conversations from chatlist
    },
    removeMessagesByIds(state, action: PayloadAction<number[]>) {
      state.message_list.Records = state.message_list.Records.filter(
        (message) => !action.payload.includes(message.message_id),
      );
      const gone = new Set(action.payload);
      state.snappingIds = state.snappingIds.filter((id) => !gone.has(id));
    },
    startMessageSnap(state, action: PayloadAction<number[]>) {
      const ids = action.payload.filter((n) => Number(n) > 0);
      state.snappingIds = [...new Set([...state.snappingIds, ...ids])];
    },
    finishMessageSnap(state, action: PayloadAction<number[]>) {
      const gone = new Set(action.payload);
      state.message_list.Records = state.message_list.Records.filter(
        (message) => !gone.has(message.message_id),
      );
      state.pinned_messages.Records = state.pinned_messages.Records.filter(
        (message) => !gone.has(message.message_id),
      );
      state.snappingIds = state.snappingIds.filter((id) => !gone.has(id));
    },
    removePinnedMessagesByIds(state, action: PayloadAction<number[]>) {
      state.pinned_messages.Records = state.pinned_messages.Records.filter(
        (message) => !action.payload.includes(message.message_id),
      );
    },
    appendMessage(state, action: PayloadAction<SingleMessageRes>) {
      const msg = action.payload.Records[0];
      if (!msg || isHiddenDeletedMessage(msg)) return;
      const existing = state.message_list.Records.find(
        (m) => m.message_id === msg.message_id,
      );
      if (existing) {
        // HTTP send often arrived first without User; socket/hydrate later
        if (!existing.User && msg.User) existing.User = msg.User;
        if (msg.ParentMessage && !existing.ParentMessage) {
          existing.ParentMessage = msg.ParentMessage;
        }
        return;
      }
      state.message_list.Records.push(msg);
    },
    // Reducer to append a single object with date check
    appendMessageWithDateCheck(state, action: PayloadAction<Message>) {
      if (isHiddenDeletedMessage(action.payload)) return;
      const existing = state.message_list.Records.find(
        (m) => m.message_id === action.payload.message_id,
      );
      if (existing) {
        if (!existing.User && action.payload.User) {
          existing.User = action.payload.User;
        }
        if (action.payload.ParentMessage && !existing.ParentMessage) {
          existing.ParentMessage = action.payload.ParentMessage;
        }
        return;
      }
      state.message_list.Records.push(action.payload);
    },

    // Upsert into pin catalog (socket delta)
    appendPinnedMessages(state, action: PayloadAction<Message>) {
      const msg = action.payload;
      if (!msg?.message_id) return;
      const idx = state.pinned_messages.Records.findIndex(
        (m) => m.message_id === msg.message_id,
      );
      if (idx >= 0) {
        state.pinned_messages.Records[idx] = {
          ...state.pinned_messages.Records[idx],
          ...msg,
          pinned: true,
        };
      } else {
        state.pinned_messages.Records.push({ ...msg, pinned: true });
      }
    },
    updateMessagesByIds(
      state,
      action: PayloadAction<{
        message_id: number;
        message?: string;
        delete_from_everyone?: boolean;
        user_id?: string;
        pinned?: boolean;
        message_seen_status?: "seen" | "sent";
        is_edited?: boolean;
        message_type?: Message["message_type"];
        link_preview?: boolean;
        link_url?: string | null;
        link_title?: string | null;
        link_description?: string | null;
        link_image?: string | null;
        link_favicon?: string | null;
        link_site_name?: string | null;
      }>,
    ) {
      const applyLinkFields = (target: Message) => {
        const p = action.payload;
        if (p.link_preview !== undefined) target.link_preview = p.link_preview;
        if (p.link_url !== undefined) target.link_url = p.link_url;
        if (p.link_title !== undefined) target.link_title = p.link_title;
        if (p.link_description !== undefined)
          target.link_description = p.link_description;
        if (p.link_image !== undefined) target.link_image = p.link_image;
        if (p.link_favicon !== undefined) target.link_favicon = p.link_favicon;
        if (p.link_site_name !== undefined)
          target.link_site_name = p.link_site_name;
      };

      const msg = state.message_list.Records.find(
        (m) => m.message_id === action.payload.message_id,
      );

      if (msg) {
        if (action.payload.message !== undefined) {
          msg.message_content = action.payload.message;
        }
        if (action.payload.delete_from_everyone !== undefined) {
          msg.deleted_for_everyone = action.payload.delete_from_everyone;
        }
        if (action.payload.pinned !== undefined) {
          msg.pinned = action.payload.pinned;
        }
        if (action.payload.message_seen_status !== undefined) {
          msg.message_seen_status = action.payload.message_seen_status;
        }
        if (action.payload.is_edited !== undefined) {
          msg.is_edited = action.payload.is_edited;
        }
        if (action.payload.message_type !== undefined) {
          msg.message_type = action.payload.message_type;
        }
        applyLinkFields(msg);
      }

      const pinned = state.pinned_messages.Records.find(
        (m) => m.message_id === action.payload.message_id,
      );
      if (pinned) {
        if (action.payload.message !== undefined) {
          pinned.message_content = action.payload.message;
        }
        if (action.payload.is_edited !== undefined) {
          pinned.is_edited = action.payload.is_edited;
        }
        if (action.payload.message_type !== undefined) {
          pinned.message_type = action.payload.message_type;
        }
        applyLinkFields(pinned);
      }
    },

    updateIsStarMessage(
      state,
      action: PayloadAction<{ message_id: number; is_star_message: boolean }>,
    ) {
      const { message_id, is_star_message } = action.payload;
      const message = state.message_list.Records.find(
        (msg) => msg.message_id == message_id,
      );

      if (message) {
        message.starred = is_star_message;
      }
    },
  },
});

// Export the reducer and actions
export default MessageListSlice.reducer;
export const {
  updateMessageList,
  replaceMessageWindow,
  replacePinnedCatalog,
  warmPinnedCatalog,
  prependOlderMessages,
  appendNewerMessages,
  removeMessagesByIds,
  startMessageSnap,
  finishMessageSnap,
  appendMessage,
  removePinnedMessagesByIds,
  appendMessageWithDateCheck,
  removeMessageList,
  updateIsStarMessage,
  updateMessagesByIds,
  appendPinnedMessages,
} = MessageListSlice.actions;
